/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useMemo, useCallback, useEffect } from 'react';
// redux
import { useSelector, useDispatch } from 'react-redux';
// utils
import { datetoAPIString, daysPreviously } from '../../../../../../utils/date';
// material
import { 
    Title, Form, Grid, GridSet, GridItem, Group, 
    TextField, FileUploader, Radio, CheckboxField,
    getDateNYC, isAfterFourPM, InlineMessage
} from '@insticator/insticator-ui'; // iui-material
import ExpandableSection from "../../../../../../material/expandablesection/expandablesection";
import SectionCard from "../../../../../../material/expandablesection/sectioncard/sectioncard";
// styles
import injectSheet from 'react-jss';
import styles from './newimport_styles';
import { DateRangePickerDropDown, SingleSelectDropDown, Tip } from '@insticator/insticator-ui'
import { createNewImport, importDisqusAPI } from '../../../../../../state/thunks/commenting';

const formName = 'newimport'
const vendors = [
  {name: 'Disqus', value: 'DISQUS'},
  {name: 'Vuukle', value: 'VUUKLE'},
  {name: 'OpenWeb', value: 'OPEN_WEB'}
];

const csvFileTypes = ['text/csv', 'application/csv', 'text/comma-separated-values', 'application/vnd.ms-excel', 'application/vnd.msexcel', 'application/excel', 'text/anytext']
const xmlJSONFileTypes = ['application/json', 'application/xml', 'text/xml']
const xmlFileTypes = ['application/xml', 'text/xml']

const generateDefaultStartDate = () => {
  let week = new Date(getDateNYC() - (isAfterFourPM() ? daysPreviously(7) : daysPreviously(8)));
  return datetoAPIString(week);
};

const generateDefaultEndDate = () => {
  let today = new Date(getDateNYC() - (isAfterFourPM() ? daysPreviously(1) : daysPreviously(2)));
  return datetoAPIString(today);
};

const initialDateRange = {
  from: generateDefaultStartDate(),
  to: generateDefaultEndDate(),
  shortcut: 'Last 7 Days'
}

/* ========== ~~~~~~~~~~ NEW IMPORT ~~~~~~~~~~ ========== */
const NewImport = props => {

  const dispatch = useDispatch()

  const { integrationId, onStartImport, onCancel } = props;

  const [selectedVendor, selectVendor] = useState('DISQUS')
  const [dateRange, setDateRange] = useState(initialDateRange)
  const [selectedDateRange, selectDateRange] = useState(initialDateRange)
  const [selectedSource, selectSource] = useState('xmlfile')
  const [isMapURLsSelected, selectMapURLs] = useState(true)
  const [isArticleAsExtPageIdSelected, selectArticleAsExtPageId] = useState(true)
  const [selectedPlan, selectPlan] = useState('BASIC')
  const [isImporting, setIsImporting] = useState(false)
  const {
      account: {
        sites = {}      
      },
      session: {
        siteUUID,
      },
    } = useSelector((state) => state);

  const siteURL = useMemo(() => sites[siteUUID] || '', [sites, siteUUID]);

  // import fields
  const [importFields, setImportFields] = useState({
    vendor: '',
    period: '',
    selectedSource: '',
    file: null,
    apiKey: '',
    siteName: ''
  })
  // initial role fields values
  const [initialFieldsState, setInitialFieldsState] = useState({...importFields})

  useEffect(() => {
    if(isImporting) window.addEventListener('beforeunload', handleBrowserTabClose);
    else window.removeEventListener('beforeunload', handleBrowserTabClose);

    // cleanup this component
    return () => {
      window.removeEventListener('beforeunload', handleBrowserTabClose);
    };
  }, [isImporting]);

  const handleBrowserTabClose = ev => {
    ev.preventDefault();
    return ev.returnValue = '';
  }

  const getDisabledDates = () => [{
    after: new Date(getDateNYC())
  }]

  const handleDateRangeApply = () => {
    selectDateRange({
      from: dateRange.from,
      to: dateRange.to,
      dateRange: 'CUSTOM',
      shortcut: ''
    })
  }

  const handleSave = () => {
    setIsImporting(true)
    let payload = {commentingUnitId: integrationId}
    if(selectedVendor === 'DISQUS') {
      handleDisqusRequest(payload)
    } else if(selectedVendor === 'VUUKLE') {
      handleVuukleRequest(payload)
    } else if(selectedVendor === 'OPEN_WEB') {
      handleSpotIMRequest(payload)
    }
  }

  const handleDisqusRequest = useCallback(async (payload) => {
    let disqusRequest = {...payload}
    let response = {}
    if(selectedSource === 'api') {
      disqusRequest = {
        ...disqusRequest,
        importType: 'DISQUS_API',
        importSource: 'DISQUS',
        apiKey: importFields.apiKey,
        siteName: importFields.siteName,
        startDate: selectedDateRange.from,
        endDate: selectedDateRange.to,
        mapUrls: false,
        disqusPlanType: selectedPlan,
        file: null,
        siteUrl: siteURL
      }
      delete disqusRequest.setExtPageId
      response = await dispatch(createNewImport(disqusRequest))
    } else {
      disqusRequest = {
        ...disqusRequest,
        file: importFields.file,
        mapUrls: isMapURLsSelected,
        importType: 'DISQUS_XML',
        importSource: 'DISQUS',
        siteUrl: siteURL
      }
      response = await dispatch(createNewImport(disqusRequest))
    }
    if(!response.error) onStartImport()
    setIsImporting(false)
  })

  const handleVuukleRequest = useCallback(async (payload) => {
    let response = {}
    payload = {
      ...payload,
      file: importFields.file,
      mapUrls: isMapURLsSelected,
      setExtPageId: isArticleAsExtPageIdSelected,
      importType: importFields.file.type.includes('json') ? 'VUUKLE_JSON' : 'VUUKLE_XML',
      importSource: 'VUUKLE',
      siteUrl: siteURL
    }
    response = await dispatch(createNewImport(payload))
    if(!response.error) onStartImport()
    setIsImporting(false)
  })

  const handleSpotIMRequest = useCallback(async (payload) => {
    let response = {}
    payload = {
      ...payload,
      file: importFields.file,
      mapUrls: isMapURLsSelected,
      importType: 'OPEN_WEB_CSV',
      importSource: 'OPEN_WEB',
      siteUrl: siteURL
    }
    delete payload.setExtPageId
    response = await dispatch(createNewImport(payload))
    if(!response.error) onStartImport()
    setIsImporting(false)
  })

  const handleCancel = () => {
    setImportFields(initialFieldsState)
    if(onCancel) onCancel()
  }

  const handleFieldsChange = (e, fieldName) => setImportFields({...importFields, [fieldName]: e.target.value})

  const isChanged = useMemo(() => {
    const isFieldsChanged = !(JSON.stringify(initialFieldsState) === JSON.stringify(importFields));
    return isFieldsChanged
  }, [importFields, initialFieldsState])

  const handleUpload = file => setImportFields({...importFields, file})

  const handleDiscard = () => setImportFields({...importFields, file: null})

  const handleSelectVendor = opt => {
    selectMapURLs(true)
    selectArticleAsExtPageId(true)
    setImportFields(initialFieldsState)
    selectVendor(opt.value)
    switch(opt.value) { 
      case 'DISQUS':
        selectSource('xmlfile')
        break;
      case 'VUUKLE': 
        selectSource('xmljsonfile')
        break;
      case 'OPEN_WEB':
        selectSource('csvfile')
        break;
    }
  }

  const disqusSource = (
  <>
    <GridSet direction={'horizontal'}>
      <GridItem>
        <Radio 
          isList={true}
          title={`XML File`}
          checked={selectedSource === 'xmlfile'}
          handleChange={() => selectSource('xmlfile')}
        />
        <div className={'sourcecontent'}>
          <FileUploader 
            uploadFile={handleUpload} 
            discardFile={handleDiscard} 
            supportedFileTypes={xmlFileTypes}
            disabled={selectedSource !== 'xmlfile'}
          />
        </div>
      </GridItem>
      <GridItem>
        <label>Import:</label>
        <CheckboxField 
          type={`checkbox`} 
          formName={formName} 
          name={`mapURLs`} 
          value={isMapURLsSelected} 
          checked={isMapURLsSelected} 
          valid
          label={`Map URLs`} 
          handleChange={() => selectMapURLs(!isMapURLsSelected)} 
          disabled={selectedSource !== 'xmlfile'}
        />
      </GridItem>
    </GridSet>    
    <GridItem>
      <Radio 
        isList={true}
        title={`API`}
        checked={selectedSource === 'api'}
        handleChange={() => selectSource('api')}
      />
      <div className={'sourcecontent'}>
        <p>Import Limits: 30MB or 500K rows</p>
        <GridSet direction={'horizontal'} name={'api'}>
          <GridItem>
            <TextField 
              type={`text`} 
              formName={formName} 
              name={`apikey`} 
              value={importFields.apiKey} 
              handleChange={e => handleFieldsChange(e, `apiKey`)} 
              valid={true} 
              label={'API Key'}
              placeholder={`Enter API Key`} 
              disabled={selectedSource !== 'api'}
              maxLength={200}
            />
          </GridItem>
          <GridItem>
            <TextField 
              type={`text`} 
              formName={formName} 
              name={`siteName`} 
              value={importFields.siteName} 
              handleChange={e => handleFieldsChange(e, `siteName`)} 
              valid={true} 
              label={'Site Name'}
              placeholder={`Enter Site Name`} 
              disabled={selectedSource !== 'api'}
              maxLength={200}
            />
          </GridItem>
        </GridSet>
        <GridSet>
        <GridItem name={'disqusplan'}>
          <label>Disqus Plan</label>
          <Radio 
            isList={true}
            title={
              <div className={`disabled-${selectedSource !== 'api'}`}>
                Basic
                <Tip 
                  space={'left'} 
                  icon={'question-circle'} 
                  content={
                    <div className={'plantip'}>
                      <h4>Basic Plan</h4>
                      <p>This plan imposes an API rate limit which restricts how much data can pulled to 1000 API requests per hour.</p> <br />
                      <p>Below please find a breakdown of how this restriction affects this import:</p> 
                      <ul style={{marginLeft: '16px'}}>
                        <li>30 thousand comments per hour</li>
                        <li>720 thousand comments per day</li>
                        <li>5.4 million comments per week</li>
                      </ul>
                      <br />
                      <p>In case you get an error due to 'Rate Limit', please select a smaller date range</p>
                    </div>
                  }
                />
              </div>
            }
            checked={selectedPlan === 'BASIC'}
            handleChange={() => selectPlan('BASIC')}
            disabled={selectedSource !== 'api'}
          />
          <Radio 
            isList={true}
            title={`Pro`}
            checked={selectedPlan === 'PRO'}
            handleChange={() => selectPlan('PRO')}
            disabled={selectedSource !== 'api'}
          />
          </GridItem>
        </GridSet>
      </div>
    </GridItem>
  </>
  )

  const vuukleSource = (
    <>
      <GridItem>
        <Radio 
          isList={true}
          title={`XML File/JSON File`}
          checked={selectedSource === 'xmljsonfile'}
          handleChange={() => selectSource('xmljsonfile')}
        />
        <div className={'sourcecontent'}>
          <FileUploader 
            uploadFile={handleUpload} 
            discardFile={handleDiscard} 
            supportedFileTypes={xmlJSONFileTypes}
            disabled={selectedSource !== 'xmljsonfile'}
          />
        </div>
      </GridItem>
      <GridItem>
        <label>Import:</label>
        <CheckboxField 
          type={`checkbox`} 
          formName={formName} 
          name={`mapURLs`} 
          value={isMapURLsSelected} 
          checked={isMapURLsSelected} 
          valid
          label={`Map URLs`} 
          handleChange={() => selectMapURLs(!isMapURLsSelected)} 
        />
        <CheckboxField 
          type={`checkbox`} 
          formName={formName} 
          name={`useArticleAsExtPageID`} 
          value={isArticleAsExtPageIdSelected} 
          checked={isArticleAsExtPageIdSelected} 
          valid
          label={`Use article as extPageID`} 
          handleChange={() => selectArticleAsExtPageId(!isArticleAsExtPageIdSelected)} 
        />
      </GridItem>
    </>
  )

  const spotIMSource = (
    <>
      <GridItem>
        <Radio 
          isList={true}
          title={`CSV File`}
          checked={selectedSource === 'csvfile'}
          handleChange={() => selectSource('csvfile')}
        />
        <div className={'sourcecontent'}>
          <FileUploader 
            uploadFile={handleUpload} 
            discardFile={handleDiscard} 
            supportedFileTypes={csvFileTypes}
            disabled={selectedSource !== 'csvfile'}
          />
        </div>
      </GridItem>
      <GridItem>
        <label>Import:</label>
        <CheckboxField 
          type={`checkbox`} 
          formName={formName} 
          name={`mapURLs`} 
          value={isMapURLsSelected} 
          checked={isMapURLsSelected} 
          valid
          label={`Map URLs`} 
          handleChange={() => selectMapURLs(!isMapURLsSelected)} 
        />
      </GridItem>
    </>
  )

  const renderVendorSource = () => {
    switch(selectedVendor) {
      case 'DISQUS':
        return disqusSource
      case 'VUUKLE':
        return vuukleSource
      case 'OPEN_WEB':
        return spotIMSource
    }
  }

  return (
    <ExpandableSection
      className={`newimport ${props.classes.newimport}`}
      controller={null}
      isOpened={true}
      isChanged={isChanged}
      isProcessing={isImporting}
      processingTitle={'Processing...'}
      disableSave={false}
      disableCancel={isImporting}
      handleCancel={handleCancel}
      handleSave={handleSave}
      saveTitle={'Start Import'}
    >
      <Form name={formName} editable={true} processing={isImporting}>
        <SectionCard>
          <Title klass={'newimporttitle'}><h2>New Import</h2></Title>
          { isImporting &&
          <InlineMessage 
            name={'uploadingwarning'}
            icon={'exclamation-triangle'} 
            variant={'secondary'} 
            title={'Warning'} 
            message={`Upload in progress. Please don't close the tab or the import will not proceed.`} 
          />
          }
          <Grid direction={`vertical`} name={`formcontainer`}>
            <GridSet direction={`horizontal`} name={'details'}>
              <GridItem>
                <label>Vendor</label>
                <SingleSelectDropDown 
                  klass={'vendordropdown'}
                  name={selectedVendor} 
                  searchable={false}
                  onSelect={handleSelectVendor} 
                  items={vendors}
                />
              </GridItem>
              { selectedVendor === 'DISQUS' && selectedSource === 'api' &&
                  <GridItem>
                  <label>Period</label>
                  <DateRangePickerDropDown
                    indicatorTitle={'Date range'}
                    hideButtons={false}
                    showShortcuts={true}
                    showShortcutTextInPreview={false}
                    customDateRanges={false}
                    use4PMLogic={true}
                    range={dateRange}
                    updateRange={range => setDateRange(range)}
                    handleApply={handleDateRangeApply}
                    disabledDays={getDisabledDates()}
                />
                </GridItem>
              }
            </GridSet>
            { renderVendorSource() }
          </Grid>
        </SectionCard>
      </Form>
    </ExpandableSection>
  );
};

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default injectSheet(styles)(NewImport);
